import React, { useEffect, useRef, useState } from "react";
import { Formik, ErrorMessage, Form as FormikForm } from "formik";
import {
  Form,
  Row,
  Col,
  Button,
  Spinner,
  ListGroup
} from "react-bootstrap";
import * as Yup from "yup";
import { getInputClasses } from "@utils/get-input-classes";
import AlertError from "@components/shared/alert-error";
import { searchIcds } from "@services/icd";
import { searchCpts } from "@services/cpt";

const SufferingForm = ({ 
  initialValues = {
    clientCertificate: "",
    atentionIden: "",
    identificator: "",
    opinionIden: "",
    icd: "",
    icdDescription: "",
    cpt: "",
    cptDescription: "",
  }, 
  axaCategories = [], 
  onSubmit, 
  disabledAll = false,
  bindSubmitForm,
  hideSubmitButton = false
}) => {
  const [icdResults, setIcdResults] = useState([]);
  const [cptResults, setCptResults] = useState([]);
  const [showIcdSuggestions, setShowIcdSuggestions] = useState(false);
  const [showCptSuggestions, setShowCptSuggestions] = useState(false);
  const [isLoadingIcd, setIsLoadingIcd] = useState(false);
  const [isLoadingCpt, setIsLoadingCpt] = useState(false);
  
  // Referencias para controlar los clics fuera de las sugerencias
  const icdSuggestionsRef = useRef(null);
  const cptSuggestionsRef = useRef(null);

  // Función para buscar ICDs
  const handleSearchIcd = async (searchTerm, setFieldValue) => {
    setIsLoadingIcd(true);
    
    if (searchTerm.length > 2) {
      try {
        const response = await searchIcds(searchTerm);
        setIcdResults(response.data || []);
        setShowIcdSuggestions(true);
      } catch (error) {
        console.error("Error searching ICDs:", error);
        setIcdResults([]);
      }
    } else {
      setFieldValue("icd", "");
      setIcdResults([]);
      setShowIcdSuggestions(false);
    }
    
    setIsLoadingIcd(false);
  };

  // Función para buscar CPTs
  const handleSearchCpt = async (searchTerm, setFieldValue) => {
    setIsLoadingCpt(true);
    
    if (searchTerm.length > 2) {
      try {
        const response = await searchCpts(searchTerm);
        setCptResults(response.data || []);
        setShowCptSuggestions(true);
      } catch (error) {
        console.error("Error searching CPTs:", error);
        setCptResults([]);
      }
    } else {
      setFieldValue("cpt", "");
      setCptResults([]);
      setShowCptSuggestions(false);
    }
    
    setIsLoadingCpt(false);
  };

  // Función para seleccionar un ICD
  const handleSelectIcd = (icd, setFieldValue) => {
    setFieldValue("icd", icd.icd || "");
    setFieldValue("icdDescription", icd.sickness || "");
    setShowIcdSuggestions(false);
  };

  // Función para seleccionar un CPT
  const handleSelectCpt = (cpt, setFieldValue) => {
    setFieldValue("cpt", cpt.cpt || "");
    setFieldValue("cptDescription", cpt.description || "");
    setShowCptSuggestions(false);
  };

  // Cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (icdSuggestionsRef.current && !icdSuggestionsRef.current.contains(event.target)) {
        setShowIcdSuggestions(false);
      }
      if (cptSuggestionsRef.current && !cptSuggestionsRef.current.contains(event.target)) {
        setShowCptSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const validationSchema = Yup.object().shape({
    // atentionIden: Yup.string().required("Campo obligatorio"),
    // identificator: Yup.string().required("Campo obligatorio"),
    icd: Yup.string().required("Campo obligatorio"),
    icdDescription: Yup.string().required("Campo obligatorio"),
  });

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{ ...initialValues, axaCategoriesArray: axaCategories }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        status,
        isSubmitting,
        submitForm,
        setFieldValue,
      }) => {
        if (bindSubmitForm) {
          bindSubmitForm(submitForm);
        }
        
        return (
          <>
            <AlertError status={status} />
            <FormikForm id="sufferingForm" onSubmit={handleSubmit}>
              <Row>
                <Col>
                  <Form.Group controlId="clientCertificate">
                    <Form.Label>Código de cliente/ Certificado</Form.Label>
                    <Form.Control
                      type="text"
                      name="clientCertificate"
                      value={values.clientCertificate}
                      onChange={handleChange}
                      disabled={disabledAll}
                      className={`${getInputClasses(
                        touched,
                        errors,
                        "clientCertificate"
                      )}`}
                    />
                    <ErrorMessage name="clientCertificate">
                      {(msg) => (
                        <Form.Control.Feedback type="invalid">
                          {msg}
                        </Form.Control.Feedback>
                      )}
                    </ErrorMessage>
                  </Form.Group>
                </Col>

                <Col>
                  <Form.Group controlId="atentionIden">
                    <Form.Label>Atención</Form.Label>
                    <Form.Control
                      type="text"
                      name="atentionIden"
                      value={values.atentionIden}
                      onChange={handleChange}
                      disabled={disabledAll}
                      className={`${getInputClasses(
                        touched,
                        errors,
                        "atentionIden"
                      )}`}
                    />
                    <ErrorMessage name="atentionIden">
                      {(msg) => (
                        <Form.Control.Feedback type="invalid">
                          {msg}
                        </Form.Control.Feedback>
                      )}
                    </ErrorMessage>
                  </Form.Group>
                </Col>

                <Col>
                  <Form.Group controlId="identificator">
                    <Form.Label>Folio</Form.Label>
                    <Form.Control
                      type="text"
                      name="identificator"
                      value={values.identificator}
                      onChange={handleChange}
                      disabled={disabledAll}
                      className={`${getInputClasses(
                        touched,
                        errors,
                        "identificator"
                      )}`}
                    />
                    <ErrorMessage name="identificator">
                      {(msg) => (
                        <Form.Control.Feedback type="invalid">
                          {msg}
                        </Form.Control.Feedback>
                      )}
                    </ErrorMessage>
                  </Form.Group>
                </Col>

                <Col>
                  <Form.Group controlId="opinionIden">
                    <Form.Label>Dictamen</Form.Label>
                    <Form.Control
                      type="text"
                      name="opinionIden"
                      value={values.opinionIden}
                      onChange={handleChange}
                      disabled={disabledAll}
                      className={`${getInputClasses(
                        touched,
                        errors,
                        "opinionIden"
                      )}`}
                    />
                    <ErrorMessage name="opinionIden">
                      {(msg) => (
                        <Form.Control.Feedback type="invalid">
                          {msg}
                        </Form.Control.Feedback>
                      )}
                    </ErrorMessage>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group controlId="icdDescription" className="position-relative">
                    <Form.Label>ICD descripción</Form.Label>
                    <Form.Control
                      type="text"
                      name="icdDescription"
                      value={values.icdDescription}
                      onChange={(e) => {
                        handleChange(e);
                        handleSearchIcd(e.target.value, setFieldValue);
                      }}
                      disabled={disabledAll}
                      className={`${getInputClasses(
                        touched,
                        errors,
                        "icdDescription"
                      )}`}
                      autoComplete="off"
                      placeholder="Escriba para buscar diagnóstico..."
                    />
                    {isLoadingIcd && (
                      <div className="position-absolute" style={{ right: "10px", top: "38px" }}>
                        <Spinner animation="border" size="sm" />
                      </div>
                    )}
                    {showIcdSuggestions && icdResults && icdResults.length > 0 && (
                      <div ref={icdSuggestionsRef} className="position-absolute w-100" style={{ zIndex: 1000 }}>
                        <ListGroup 
                          className="shadow-sm" 
                          style={{ maxHeight: "200px", overflowY: "auto" }}
                        >
                          {icdResults.map((icd, index) => (
                            <ListGroup.Item 
                              key={index} 
                              action 
                              onClick={() => handleSelectIcd(icd, setFieldValue)}
                            >
                              <strong>{icd.icd}</strong> - {icd.sickness}
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      </div>
                    )}
                    <ErrorMessage name="icdDescription">
                      {(msg) => (
                        <Form.Control.Feedback type="invalid">
                          {msg}
                        </Form.Control.Feedback>
                      )}
                    </ErrorMessage>
                  </Form.Group>
                </Col>

                <Col>
                  <Form.Group controlId="icd">
                    <Form.Label>ICD (Clave)</Form.Label>
                    <Form.Control
                      type="text"
                      name="icd"
                      value={values.icd}
                      onChange={handleChange}
                      disabled={true}
                      readOnly
                      className={`${getInputClasses(
                        touched,
                        errors,
                        "icd"
                      )}`}
                      placeholder="Se completará automáticamente"
                    />
                    <ErrorMessage name="icd">
                      {(msg) => (
                        <Form.Control.Feedback type="invalid">
                          {msg}
                        </Form.Control.Feedback>
                      )}
                    </ErrorMessage>
                  </Form.Group>
                </Col>

                <Col>
                  <Form.Group controlId="cptDescription" className="position-relative">
                    <Form.Label>CPT descripción</Form.Label>
                    <Form.Control
                      type="text"
                      name="cptDescription"
                      value={values.cptDescription}
                      onChange={(e) => {
                        handleChange(e);
                        handleSearchCpt(e.target.value, setFieldValue);
                      }}
                      disabled={disabledAll}
                      className={`${getInputClasses(
                        touched,
                        errors,
                        "cptDescription"
                      )}`}
                      autoComplete="off"
                      placeholder="Escriba para buscar procedimiento..."
                    />
                    {isLoadingCpt && (
                      <div className="position-absolute" style={{ right: "10px", top: "38px" }}>
                        <Spinner animation="border" size="sm" />
                      </div>
                    )}
                    {showCptSuggestions && cptResults && cptResults.length > 0 && (
                      <div ref={cptSuggestionsRef} className="position-absolute w-100" style={{ zIndex: 1000 }}>
                        <ListGroup 
                          className="shadow-sm" 
                          style={{ maxHeight: "200px", overflowY: "auto" }}
                        >
                          {cptResults.map((cpt, index) => (
                            <ListGroup.Item 
                              key={index} 
                              action 
                              onClick={() => handleSelectCpt(cpt, setFieldValue)}
                            >
                              <strong>{cpt.cpt}</strong> - {cpt.description}
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      </div>
                    )}
                    <ErrorMessage name="cptDescription">
                      {(msg) => (
                        <Form.Control.Feedback type="invalid">
                          {msg}
                        </Form.Control.Feedback>
                      )}
                    </ErrorMessage>
                  </Form.Group>
                </Col>

                <Col>
                  <Form.Group controlId="cpt">
                    <Form.Label>CPT (Clave)</Form.Label>
                    <Form.Control
                      type="text"
                      name="cpt"
                      value={values.cpt}
                      onChange={handleChange}
                      disabled={true}
                      readOnly
                      className={`${getInputClasses(
                        touched,
                        errors,
                        "cpt"
                      )}`}
                      placeholder="Se completará automáticamente"
                    />
                    <ErrorMessage name="cpt">
                      {(msg) => (
                        <Form.Control.Feedback type="invalid">
                          {msg}
                        </Form.Control.Feedback>
                      )}
                    </ErrorMessage>
                  </Form.Group>
                </Col>
              </Row>

              {!hideSubmitButton && (
                <Button className="mb-4" variant="primary" type="submit" disabled={isSubmitting || disabledAll}>
                  {isSubmitting && (
                    <Spinner
                      as="span"
                      animation="border"
                      variant="dark"
                      size="sm"
                    />
                  )}
                  Guardar
                </Button>
              )}
            </FormikForm>
          </>
        );
      }}
    </Formik>
  );
};

export default SufferingForm;

